import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  Alert,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import { TikTok as TikTokIcon } from '../../ui/CustomIcons';

// Platform configurations
const PLATFORMS = {
  instagram: { name: 'Instagram', icon: <InstagramIcon fontSize="small" />, aspectRatio: '4/5' },
  facebook: { name: 'Facebook', icon: <FacebookIcon fontSize="small" />, aspectRatio: '1/1' },
  twitter: { name: 'Twitter', icon: <TwitterIcon fontSize="small" />, aspectRatio: '16/9' },
  linkedin: { name: 'LinkedIn', icon: <LinkedInIcon fontSize="small" />, aspectRatio: '1.91/1' },
  tiktok: { name: 'TikTok', icon: <TikTokIcon fontSize="small" />, aspectRatio: '9/16' },
  youtube: { name: 'YouTube', icon: <YouTubeIcon fontSize="small" />, aspectRatio: '16/9' }
};

const GridConfigDialog = ({ open, onClose, config, onSave }) => {
  const [localConfig, setLocalConfig] = useState({
    rowCount: config.rowCount || 4,
    cellMode: config.cellMode || 'single',
    platforms: config.platforms || ['instagram'], // Default to Instagram
    defaultPlatform: config.defaultPlatform || 'instagram'
  });

  // Ensure default platform is always in selected platforms
  const handlePlatformToggle = (platform) => {
    const newPlatforms = localConfig.platforms.includes(platform)
      ? localConfig.platforms.filter(p => p !== platform)
      : [...localConfig.platforms, platform];
    
    // If removing the default platform, set new default to first selected
    let newDefaultPlatform = localConfig.defaultPlatform;
    if (!newPlatforms.includes(localConfig.defaultPlatform) && newPlatforms.length > 0) {
      newDefaultPlatform = newPlatforms[0];
    }
    
    setLocalConfig({
      ...localConfig,
      platforms: newPlatforms,
      defaultPlatform: newDefaultPlatform
    });
  };

  const handleSave = () => {
    onSave(localConfig);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Grid Configuration</DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Number of Rows
          </Typography>
          <TextField
            type="number"
            value={localConfig.rowCount}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (value >= 1 && value <= 10) {
                setLocalConfig({ ...localConfig, rowCount: value });
              }
            }}
            inputProps={{ min: 1, max: 10 }}
            size="small"
            fullWidth
            helperText="Between 1 and 10 rows"
          />
        </Box>

        <FormControl component="fieldset">
          <FormLabel component="legend">Cell Mode</FormLabel>
          <RadioGroup
            value={localConfig.cellMode}
            onChange={(e) => setLocalConfig({ ...localConfig, cellMode: e.target.value })}
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">Single Task</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Each cell can contain only one task
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="multiple"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">Multiple Tasks</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Each cell can contain multiple tasks
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* Platform Configuration */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Enabled Platforms
          </Typography>
          <FormGroup>
            {Object.entries(PLATFORMS).map(([key, platform]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={localConfig.platforms.includes(key)}
                    onChange={() => handlePlatformToggle(key)}
                    disabled={localConfig.platforms.length === 1 && localConfig.platforms.includes(key)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {platform.icon}
                    <Typography variant="body2">{platform.name}</Typography>
                    <Chip
                      label={platform.aspectRatio}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                }
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Select the platforms you want to manage. Each platform will have its own grid.
          </Typography>
        </Box>

        {/* Default Platform Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Default Platform
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={localConfig.defaultPlatform}
              onChange={(e) => setLocalConfig({ ...localConfig, defaultPlatform: e.target.value })}
            >
              {localConfig.platforms.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {PLATFORMS[platform].icon}
                    <Typography>{PLATFORMS[platform].name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            This platform will be shown by default when opening the grid.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            Note: The grid always has 3 columns to match social media grid layouts.
            Currently {localConfig.rowCount * 3} posts can be displayed.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          magical={true}
          disabled={localConfig.platforms.length === 0}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GridConfigDialog;